import React from 'react'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, LabelList } from 'recharts'

import ChartTooltip from './ChartTooltip'
import Pager from './Pager'
import usePagination, { DEFAULT_PAGE_SIZE } from './usePagination'

const AXIS_BAND = 56
const MIN_HEIGHT = 220

/**
 * Horizontal stacked bar -- part-to-whole across long-named categories.
 *
 * Horizontal because branch and user names are long; rotating them to fit a
 * column chart is what made the previous version hard to read.
 *
 * A series may name its own `stack`, which puts it on a second bar for the same
 * category. That matters when the same colour appears in both stacks (liability
 * created and liability updated): two segments of one hue touching inside a
 * single stack would be unreadable, whereas two separate bars are not.
 *
 * `legendPayload` overrides the derived legend, so a chart with four series but
 * only two colours can show two entries instead of four.
 *
 * Rows are paged so the card keeps a bounded height. The data arrives sorted
 * largest-first, so page one is the top N.
 */
const HorizontalStackedBar = ({
    data,
    series,
    yKey = 'name',
    showTotals = true,
    legendPayload,
    rowHeight = 30,
    showLegend = true,
    pageSize = DEFAULT_PAGE_SIZE,
    unit = 'rows',
}) => {
    const { pageItems, page, pageCount, setPage, start, total } = usePagination(data, pageSize)
    const stacks = [...new Set(series.map((item) => item.stack || 'stack'))]

    // Height follows the page, not the whole dataset, and includes the x-axis
    // band so the scale is never cut off into a nested scrollbar.
    const height = Math.max(MIN_HEIGHT, pageItems.length * rowHeight * stacks.length + AXIS_BAND)

    // The final series of each stack carries the rounded data-end.
    const lastOfStack = stacks.reduce((acc, stack) => {
        const members = series.filter((item) => (item.stack || 'stack') === stack)
        acc[stack] = members[members.length - 1]?.key
        return acc
    }, {})

    return (
        <>
            <ResponsiveContainer width="100%" height={height}>
                <BarChart data={pageItems} layout="vertical" margin={{ top: 4, right: 48, left: 8, bottom: 4 }} barGap={2}>
                    <CartesianGrid horizontal={false} stroke="#e1e0d9" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#898781' }} stroke="#c3c2b7" />
                    <YAxis
                        type="category"
                        dataKey={yKey}
                        width={150}
                        interval={0}
                        tick={{ fontSize: 11, fill: '#52514e' }}
                        stroke="#c3c2b7"
                    />
                    <Tooltip content={<ChartTooltip total />} cursor={{ fill: 'rgba(11,11,11,0.04)' }} />
                    {showLegend && <Legend wrapperStyle={{ fontSize: 12 }} payload={legendPayload} />}
                    {series.map((item) => {
                        const stack = item.stack || 'stack'
                        const isLast = lastOfStack[stack] === item.key
                        return (
                            <Bar
                                key={item.key}
                                dataKey={item.key}
                                name={item.label}
                                stackId={stack}
                                fill={item.color}
                                stroke="#ffffff"
                                strokeWidth={2}
                                barSize={20}
                                radius={isLast ? [0, 4, 4, 0] : 0}
                            >
                                {showTotals && isLast && stacks.length === 1 && (
                                    <LabelList dataKey="total" position="right" className="viz-bar-label" />
                                )}
                            </Bar>
                        )
                    })}
                </BarChart>
            </ResponsiveContainer>
            <Pager
                page={page}
                pageCount={pageCount}
                setPage={setPage}
                start={start}
                count={pageItems.length}
                total={total}
                unit={unit}
            />
        </>
    )
}

export default HorizontalStackedBar
